import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { useQboReadiness } from '../../hooks/useQboReadiness';
import { qboLineItemTypeLabel } from '../../lib/qbo-line-item-label';
import type { QboReadinessBlocker } from '../../types/qbo-mapping';

/**
 * PUSH READINESS, ABOVE THE TABS (API #887).
 *
 * The push is OFF for a company until every line on its non-void invoices
 * resolves to a QuickBooks item and every tax it charges has a code. This card
 * says which of those is still missing and links each to the tab that fixes it.
 * It reads `['qbo', 'readiness']`, so every save on this page (which
 * invalidates `['qbo']`) refreshes it.
 *
 * Four states, and a load error is NOT one of the other three: it does not say
 * "ready" and it does not render an empty blocker list. Not connected renders
 * nothing — the page's own not-connected state already covers it.
 *
 * Customers are not a blocker (a customer binding refuses only that invoice, at
 * push time), so they are a secondary, non-blocking line.
 */

type BlockerKind = QboReadinessBlocker['kind'];
type TabKind = BlockerKind | 'customer';

/** The ?kind= each blocker deep-links to. One map, so no row can hard-code another's tab. */
const TAB_FOR_BLOCKER: Record<BlockerKind, TabKind> = {
  item: 'item',
  rate_sheet_item: 'rate_sheet_item',
  tax_code: 'tax_code',
};

const TAB_LABEL: Record<TabKind, string> = {
  item: 'Line items',
  rate_sheet_item: 'Rate sheet rows',
  tax_code: 'Tax codes',
  customer: 'Customers',
};

export default function QboReadinessCard({ onSelectKind }: { onSelectKind: (kind: TabKind) => void }) {
  const readiness = useQboReadiness();

  /** A link that switches the tab in place (no reload) and still has a real href. */
  const tabLink = (kind: TabKind, text: React.ReactNode, testId: string) => (
    <Link
      to={`?kind=${kind}`}
      data-testid={testId}
      onClick={(e) => { e.preventDefault(); onSelectKind(kind); }}
      className="shrink-0 text-sm font-semibold text-fieldiq-accent hover:underline"
    >
      {text}
    </Link>
  );

  // Disabled (not admin/office) — the push is not theirs; say nothing.
  if (readiness.fetchStatus === 'idle' && readiness.status === 'pending') return null;

  if (readiness.isLoading) {
    return (
      <div data-testid="qbo-readiness" data-state="loading" className="flex items-center gap-2 text-fieldiq-muted text-sm">
        <Loader2 size={16} className="animate-spin" /> Checking whether QuickBooks push is on…
      </div>
    );
  }

  if (readiness.isError || !readiness.data) {
    return (
      <div
        data-testid="qbo-readiness"
        data-state="error"
        role="alert"
        className="flex items-start gap-2 bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 text-sm text-fieldiq-text"
      >
        <AlertCircle size={16} className="mt-0.5 shrink-0 text-fi-signal-red" />
        <span>Couldn't load whether QuickBooks push is on. Push status: —</span>
      </div>
    );
  }

  const r = readiness.data;
  if (!r.connected) return null;

  const customers = r.unmappedCustomers;
  const customerLine = customers && customers.length > 0 && (
    <div data-testid="qbo-readiness-customers" className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3 text-sm text-fieldiq-muted">
      <span className="flex-1">
        {customers.length === 1
          ? '1 customer isn’t mapped yet. Their invoices will be refused until it is.'
          : `${customers.length} customers aren’t mapped yet. Their invoices will be refused until they are.`}
      </span>
      {tabLink('customer', `Open ${TAB_LABEL.customer}`, 'qbo-readiness-link-customer')}
    </div>
  );

  if (r.pushEnabled === true) {
    return (
      <div data-testid="qbo-readiness" data-state="ready" className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-2">
        <p className="flex items-center gap-2 text-sm text-fieldiq-text">
          <Check size={16} className="shrink-0 text-green-400" />
          QuickBooks push is on. Every line type and tax in use is mapped.
        </p>
        {customerLine}
      </div>
    );
  }

  return (
    <div data-testid="qbo-readiness" data-state="not_ready" className="bg-fieldiq-card border border-fieldiq-accent/40 rounded-xl p-4 space-y-3">
      <p className="flex items-center gap-2 text-sm font-semibold text-fieldiq-text">
        <AlertCircle size={16} className="shrink-0 text-fieldiq-accent" />
        QuickBooks push is off until these are mapped
      </p>
      <ul className="space-y-3">
        {r.blockers.map((b) => (
          <li
            key={b.kind}
            data-testid={`qbo-blocker-${b.kind}`}
            className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3 border-t border-fieldiq-border/50 pt-3"
          >
            <div className="flex-1 min-w-0 space-y-2">
              <p className="text-sm text-fieldiq-text">
                {b.message}{' '}
                <span data-testid={`qbo-blocker-count-${b.kind}`} className="text-xs text-fieldiq-muted">
                  ({b.missingKeys.length})
                </span>
              </p>
              {/* Rate-sheet row ids mean nothing to a person — the tab shows the rows. */}
              {b.kind !== 'rate_sheet_item' && b.missingKeys.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {b.missingKeys.map((k) => (
                    <span
                      key={k}
                      data-testid="qbo-blocker-chip"
                      title={k}
                      className={`max-w-full break-words rounded-md bg-fieldiq-dark border border-fieldiq-border px-2 py-0.5 text-xs text-fieldiq-text${b.kind === 'item' ? ' capitalize' : ''}`}
                    >
                      {/* Same label and case as the Line items tab's row for this key. */}
                      {b.kind === 'item' ? qboLineItemTypeLabel(k) : k}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {tabLink(TAB_FOR_BLOCKER[b.kind], `Open ${TAB_LABEL[TAB_FOR_BLOCKER[b.kind]]}`, `qbo-readiness-link-${b.kind}`)}
          </li>
        ))}
      </ul>
      {customerLine}
    </div>
  );
}
